import { ArtistCard, ArtistList, Skeleton } from '@design-system';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { FC, useCallback, useEffect, useState } from 'react';

import { csrClient } from '../../apollo/api/avantArteClient';
import { AvantArteArtistsSearchGateway } from '../../gateways/AvantArteArtistsSearchGateway';
import {
  ArtistIdStoryblok,
  RichtextStoryblok,
} from '../../types/generated-storyblok';
import { useAnalyticsEmitter } from '../../utils/analytics/emitter';
import { getArtistTileOverline } from '../../utils/artists';
import { fetchAllProducts } from '../../utils/shopify/fetchAllProducts';
import { fetchStories } from '../../utils/storyblok/stories';
import { ArtistSubscription } from '../Content/ArtistSubscription/ArtistSubscription';

const artistsGateway = new AvantArteArtistsSearchGateway(csrClient);

interface Props {
  blok: ArtistIdStoryblok;
  variant: 'card' | 'list';
}

export const ArticleArtist: FC<Props> = ({ blok, variant }) => {
  const { t } = useTranslation('artists');
  const [emitter] = useAnalyticsEmitter();

  const [artist, setArtist] = useState<ArtistPageContent>();
  const [loading, setLoading] = useState(true);
  const [internalId, setInternalId] = useState('');
  const [workCount, setWorkCount] = useState(0);

  const handleClick = useCallback(() => {
    emitter('', 'artist clicked', {
      'artist clicked name': artist?.name,
    });
  }, [artist, emitter]);

  useEffect(() => {
    fetchStories({
      byUuids: 'id' in blok.artist ? blok.artist?.id : undefined,
      page: 1,
      perPage: 1,
    }).then(([story]: ArtistPageContent[]) => {
      if (!story) {
        return;
      }
      setArtist(story);
      artistsGateway.getArtistsBySlug([story.slug]).then(([result]) => {
        setInternalId(result.id);
      });
      fetchAllProducts(
        `tag:'${story.content.name}' AND tag:'Listing page'`,
      ).then((works) => {
        setWorkCount(works.length);
        setLoading(false);
      });
    });
  }, [blok.artist]);

  if (loading || !artist) {
    return variant === 'card' ? (
      <div className="flex flex-col overflow-hidden rounded-sm border border-neutral-1">
        <Skeleton className="aspect-[4/3]" />
        <div className="flex flex-col gap-1 p-4 md:p-6">
          <Skeleton className="h-9 w-3/4 md:h-5" />
          <Skeleton className="h-9 w-1/2 md:h-5" />
        </div>
      </div>
    ) : (
      <div className="flex w-full items-center gap-3 pt-3 first:pt-0 md:pt-4">
        <Skeleton className="h-16 w-14 shrink-0" />
        <div className="flex w-full flex-col gap-1">
          <Skeleton className="h-9 w-3/4 md:h-5" />
          <Skeleton className="h-9 w-1/2 md:h-5" />
        </div>
      </div>
    );
  }

  const SubscriptionButton = (
    <ArtistSubscription
      buttonLocation="story"
      internalId={internalId}
      name={artist?.name}
      thumbnail={artist.content.thumbnail.filename}
    />
  );

  if (variant === 'card') {
    return (
      <div className="relative">
        <Link
          className="group overflow-hidden rounded-sm"
          href={`/${artist.full_slug}`}
          onClick={handleClick}
          prefetch={false}
        >
          <ArtistCard
            description={artist.content?.practiceOverview as RichtextStoryblok}
            image={artist.content?.thumbnail}
            name={artist.name}
            overline={getArtistTileOverline(
              workCount,
              artist.content.hideCollaborations || null,
              t,
            )}
          />
        </Link>

        <div className="absolute right-4 top-4">{SubscriptionButton}</div>
      </div>
    );
  }

  return (
    <div className="align-center relative flex w-full justify-center gap-x-3 overflow-hidden pt-3 first:pt-0 md:pt-4">
      <Link
        className="group w-full overflow-hidden"
        href={`/${artist.full_slug}`}
        onClick={handleClick}
        prefetch={false}
      >
        <ArtistList
          image={artist.content?.thumbnail}
          name={artist.name}
          overline={getArtistTileOverline(
            workCount,
            artist.content.hideCollaborations || null,
            t,
          )}
        />
      </Link>

      <div className="flex shrink-0 items-center justify-center">
        {SubscriptionButton}
      </div>
    </div>
  );
};
